import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate, type Location } from 'react-router-dom';
import { useUserAuth } from '../../contexts/UserAuthContext';
import { supabase } from '../../lib/supabase';
import { getPostAuthRedirect } from '../../utils/postAuthRedirect';
import { LEGAL_DOCUMENT_VERSION } from '../../pages/legal/legalVersion';
import { PENDING_CONSENTS_KEY, type PendingConsentRow } from '../../contexts/UserAuthContext';
import styles from '../../pages/UserLoginPage.module.css';

interface SignupFormProps {
  embedded?: boolean;
  onLoginSuccessSamePage?: (destination: string) => boolean;
}

/**
 * SignupForm — extracted from UserSignupPage so it can render inside both
 * the standalone page and the AuthModal shell.
 *
 * Auth behavior (email + password + Supabase resend confirmation) is unchanged.
 */
export function SignupForm({ embedded = false, onLoginSuccessSamePage }: SignupFormProps) {
  const { user, isLoading, signUpWithEmail } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const passThroughState = useMemo(() => {
    const s = location.state as { backgroundLocation?: Location } | null;
    return s?.backgroundLocation ? { backgroundLocation: s.backgroundLocation } : undefined;
  }, [location.state]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [signedUpEmail, setSignedUpEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setResendNotice(null);

      const trimmed = email.trim();
      if (!trimmed) {
        setError('Inserisci un indirizzo email');
        return;
      }
      if (password.length < 8) {
        setError('La password deve contenere almeno 8 caratteri');
        return;
      }
      if (password !== confirmPassword) {
        setError('Le password non coincidono');
        return;
      }
      if (!acceptedTerms || !acceptedPrivacy) {
        setError('Devi accettare Termini di servizio e Privacy Policy per registrarti');
        return;
      }

      setSubmitting(true);
      try {
        await signUpWithEmail(trimmed, password);
        // Consent capture is deferred: signUpWithEmail returns no session while
        // email confirmation is pending, and POST /api/consents requires a
        // confirmed session (401/403 otherwise). Stash the choice here and let
        // UserAuthContext flush it on the first SIGNED_IN after confirmation.
        const pendingConsents: PendingConsentRow[] = [
          { kind: 'terms', granted: acceptedTerms, documentVersion: LEGAL_DOCUMENT_VERSION },
          { kind: 'privacy', granted: acceptedPrivacy, documentVersion: LEGAL_DOCUMENT_VERSION },
        ];
        try {
          window.localStorage.setItem(PENDING_CONSENTS_KEY, JSON.stringify(pendingConsents));
        } catch {
          // localStorage unavailable (private mode / quota) — consent flush will
          // simply have nothing to send later; signup itself still succeeds.
        }
        setSignedUpEmail(trimmed);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore in fase di registrazione');
      } finally {
        setSubmitting(false);
      }
    },
    [email, password, confirmPassword, acceptedTerms, acceptedPrivacy, signUpWithEmail]
  );

  const handleResend = useCallback(async () => {
    if (!signedUpEmail) return;
    setResendNotice(null);
    setResending(true);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: signedUpEmail,
      });
      if (resendError) throw resendError;
      setResendNotice('Email di conferma inviata di nuovo.');
    } catch (err) {
      setResendNotice(err instanceof Error ? err.message : "Impossibile reinviare l'email");
    } finally {
      setResending(false);
    }
  }, [signedUpEmail]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void getPostAuthRedirect(user.id).then((dest) => {
      if (cancelled) return;
      if (onLoginSuccessSamePage && onLoginSuccessSamePage(dest)) return;
      navigate(dest, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [user, navigate, onLoginSuccessSamePage]);

  if (isLoading) return null;

  let inner: React.ReactNode;

  if (signedUpEmail) {
    inner = (
      <>
        <div className={styles.header}>
          <h1 className={styles.logo}>Buzzmap</h1>
          <p className={styles.subtitle}>Quasi fatto</p>
        </div>

        <p className={styles.message}>
          Controlla la tua email <strong>{signedUpEmail}</strong> per confermare l&apos;account.
        </p>

        {resendNotice && (
          <p className={styles.success} role="status">
            {resendNotice}
          </p>
        )}

        <button
          type="button"
          className={styles.secondaryButton}
          onClick={handleResend}
          disabled={resending}
        >
          {resending ? 'Invio in corso...' : 'Reinvia email'}
        </button>

        <div className={styles.links}>
          <Link to="/accedi" state={passThroughState} className={styles.link}>
            Torna al login
          </Link>
        </div>
      </>
    );
  } else {
    inner = (
      <>
        <div className={styles.header}>
          <h1 className={styles.logo}>Buzzmap</h1>
          <p className={styles.subtitle}>Crea il tuo account</p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label htmlFor="signup-email" className={styles.label}>
              Email
            </label>
            <input
              id="signup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@esempio.it"
              className={styles.input}
              autoComplete="email"
              disabled={submitting}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="signup-password" className={styles.label}>
              Password
            </label>
            <input
              id="signup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Almeno 8 caratteri"
              className={styles.input}
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="signup-password-confirm" className={styles.label}>
              Conferma password
            </label>
            <input
              id="signup-password-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Ripeti la password"
              className={styles.input}
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="signup-accept-terms" className={styles.checkboxLabel}>
              <input
                id="signup-accept-terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={submitting}
              />
              Accetto i{' '}
              <Link to="/termini" target="_blank" rel="noopener noreferrer" className={styles.link}>
                Termini di servizio
              </Link>
            </label>
            <label htmlFor="signup-accept-privacy" className={styles.checkboxLabel}>
              <input
                id="signup-accept-privacy"
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                disabled={submitting}
              />
              Accetto la{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className={styles.link}>
                Privacy Policy
              </Link>
            </label>
          </div>

          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={submitting || !acceptedTerms || !acceptedPrivacy}
          >
            {submitting ? (
              <>
                <span className={styles.spinner} />
                Registrazione...
              </>
            ) : (
              'Registrati'
            )}
          </button>
        </form>

        <div className={styles.links}>
          <span className={styles.signup}>
            Hai gia un account?{' '}
            <Link to="/accedi" state={passThroughState} className={styles.link}>
              Accedi
            </Link>
          </span>
          <Link to="/accedi" state={passThroughState} className={styles.link}>
            Registrati con Google
          </Link>
        </div>
      </>
    );
  }

  if (embedded) return <>{inner}</>;

  return <div className={styles.card}>{inner}</div>;
}
